
import axiosInstance from './axiosInstance';

import type { Response } from './axiosInstance';
import { jobEnity } from './job';

export type ChatRequest = {
    session_id?: string | null;
    message: string;
    working_directory: ContextEntry[];
    client_action_id?: string;
    output_directory?: string | null;
    molecule_file_path?: string | null;
    host?: string;
    port?: string;
};

export type ContextEntry = {
    type: 'file' | 'folder';
    name: string;
    path: string;
    isPined?: boolean;
};

export type ContinueRequest = {
    session_id: string;
    action_id?: string | null;
    user_input?: string;
    message?: string | null;
    additional_input?: string | null;
    host?: string;
    port?: string;
};

export type ChatResponse = {
    session_id: string;
    response: string;
    status: "completed" | "waiting_confirmation" | "error";
    requires_user_input?: boolean;
    confirmation_data?: Record<string, any> | null;
    suggested_actions?: string[];
    metadata?: Record<string, any> & {
        task_id?: string | null;
    };
    detail?: string | null;
};

export const chat = async (request: ChatRequest): Promise<ChatResponse | undefined> => {
    try {
        let token = localStorage.getItem('token');
        // 过滤掉 host 和 port 字段
        const { host, port, ...originalParams } = request;
        const response = await fetch(import.meta.env.VITE_API_URL + '/agent/chat', {                 // Fetch message response from API
            method: 'POST',
            headers: {
                'Agent-IP': host || '',
                'Agent-Port': port || '',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(originalParams)
        });
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw undefined;
        }

    } catch (e) {
        console.error(e);
        return undefined;
    }
};

export const continueChat = async (request: ContinueRequest): Promise<ChatResponse | undefined> => {
    try {
        let token = localStorage.getItem('token');
        const { host, port, ...originalParams } = request;
        const response = await fetch(import.meta.env.VITE_API_URL + '/agent/continue', {                 // Fetch message response from API
            method: 'POST',
            headers: {
                'Agent-IP': host || '',
                'Agent-Port': port || '',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(originalParams)
        });
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw undefined;
        }

    } catch (e) {
        console.error(e);
        return undefined;
    }
};


type SendParams = {
    message: string;
    host?: string;
    port?: string;
    session_id?: string | null;
    current_file_path?: string | null;
}

export type NormalResponse = {
    session_id: string;
    response: string;
    status: "success" | "error";
    requires_user_input?: boolean;
    confirmation_data?: Record<string, any> | null;
    suggested_actions?: string[];
    event_type?: "message" | "tool_execution"
    metadata?: Record<string, any> & {
        task_id?: string | null;
    };
    detail?: string | null;
};

export const send = async (params: SendParams): Promise<NormalResponse | undefined> => {
    try {
        let token = localStorage.getItem('token');
        const { host, port, ...originalParams } = params;
        const response = await fetch(import.meta.env.VITE_API_URL + '/llm/send', {                 // Fetch message response from API
            method: 'POST',
            headers: {
                'Agent-IP': host || '',
                'Agent-Port': port || '',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(originalParams)
        });
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw undefined;
        }
    } catch (e) {
        return undefined;
    }
};

export type SaveMoleculesParams = {
    session_id: string;
    target_folder_path?: string;
    folder_name?: string;
    host?: string;
    port?: string;
};

export type SaveMoleculesResponse = {
    session_id: string;
    response: string;
    message: string;
    status: "success" | "error";
    requires_user_input?: boolean;
    confirmation_data?: Record<string, any> | null;
    suggested_actions?: string[];
    event_type?: "message" | "tool_execution"
    metadata?: Record<string, any> & {
        task_id?: string | null;
    };
    detail?: string | null;
};

export const saveMolecules = async (params: SaveMoleculesParams): Promise<SaveMoleculesResponse | undefined> => {
    try {
        let token = localStorage.getItem('token');
        const { host, port, ...originalParams } = params;
        const response = await fetch(import.meta.env.VITE_API_URL + '/llm/send', {
            method: 'POST',
            headers: {
                'Agent-IP': host || '',
                'Agent-Port': port || '',
                'Agent-Route': '/api/save-molecules',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(originalParams)
        });
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw undefined;
        }
    } catch (e) {
        return undefined;
    }
};

export type runParams = {
    session_id: string;
    host?: string;
    port?: string;
}

export type runResponse = {
    session_id: string;
    response: string;
    message: string;
    event_status: 'running' | 'completed' | 'error';
    requires_user_input?: boolean;
    confirmation_data?: Record<string, any> | null;
    suggested_actions?: string[];
    event_type?: "message" | "tool_execution"
    metadata?: Record<string, any> & {
        task_id?: string | null;
    };
    detail?: string | null;
}

export const run = async (params: runParams): Promise<runResponse | undefined> => {
    try {
        let token = localStorage.getItem('token');
        const { host, port, ...originalParams } = params;
        const response = await fetch(import.meta.env.VITE_API_URL + '/llm/send', {
            method: 'POST',
            headers: {
                'Agent-IP': host || '',
                'Agent-Port': port || '',
                'Agent-Route': '/api/submit',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(originalParams)
        });
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            throw undefined;
        }
    } catch (e) {
        return undefined;
    }
};

export const getStructure = async (name: string): Promise<Response<string> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<string> | undefined>('/llm/getStructure', {
            params: {
                name: name
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
};

export const launchLLMJob = async (params: { cpu: number, version?: string, gpu: number, partition?: string, node?: string, maxTime?: number }): Promise<Response<undefined> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<undefined>>('/llm/launch', {
            params: {
                cpuNum: params.cpu ?? 1,
                gpuNum: params.gpu ?? 0,
                version: params.version ?? 'experimental',
                partition: params.partition,
                node: params.node,
                max_time: params.maxTime
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
}

export const checkLLMJob = async (): Promise<Response<jobEnity> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<jobEnity>>('/llm/check', {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
}

export const getLLMJobById = async (jobId: number): Promise<Response<string> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<string>>(`/llm/route`, {
            params: {
                jobId
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
};


export type CurrentMonthTokenBillingStatus = {
    is_over_limit: boolean;
    next_reset_at: string;
    current_amount_micros: number;
    weekly_limit_micros: number;
}

export const getCurrentMonthTokenBillingStatus = async (): Promise<CurrentMonthTokenBillingStatus | undefined> => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(import.meta.env.VITE_API_URL + '/token-billing/me/current-week/status', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            return undefined;
        }

        const result = await response.json();
        if (!result?.ok || !result?.data) {
            return undefined;
        }

        return result.data;
    } catch (error) {
        console.error('Failed to fetch token billing status:', error);
        return undefined;
    }
}




export type Model = {
    id: number;
    name: string;
    display_name: string;
    description: string;
    sort_order: number;
    is_enable: boolean;
    config: string;
}

export const llmModelListForUsers = async (): Promise<Response<Model[]> | undefined> => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(import.meta.env.VITE_API_URL + '/llm-models/me', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            return undefined;
        }

        return await response.json() as Response<Model[]>;
    }
    catch (error) {
        return undefined;
    }
}